import React, { useMemo, useState, useEffect } from 'react';
import { useBase } from '@/lib/useBase';
import { StaffBankModal } from '@/features/shared/finance/StaffBankModal';
import { PausePayModal } from '@/features/shared/finance/PausePayModal';
import { useAuth } from '@/stores/auth';
import { api } from '@/lib/api';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getPayrollRuns, getPayrollRunById, postPayrollRun,
  computePayrollRun, submitPayrollRun, approvePayrollRun, payPayrollRun,
  getPayrollSalaries, putPayrollSalary, deletePayrollSalary, getSalaryGrades, postSalaryGrade,
  getPayrollAllowances, getPayrollDeductions,
  postPayrollAllowance, postPayrollDeduction, postPayrollIncrement,
  getPayrollHistory,
} from '@/api/owner';
import {
  Plus, X, AlertCircle, Loader2, RefreshCw, Play, Send, Check,
  Wallet, Users, DollarSign, FileText, Edit2, ChevronRight, Banknote,
} from 'lucide-react';
import {ConfirmModal, TextPromptModal, FormModal, type FormField, PinModal } from '@/components/ui/prompts';

const arr = <T,>(src: any): T[] => {
  if (!src) return [];
  if (Array.isArray(src)) return src;
  if (Array.isArray(src.data)) return src.data;
  if (Array.isArray(src.data?.data)) return src.data.data;
  return [];
};
const unwrap = (r: any) => r?.data?.data ?? r?.data;
const money = (n: any) => {
  if (n == null || n === '') return '—';
  return '₦' + Number(n).toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};
const fmtDate = (d?: string | null) =>
  d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

const statusTone: Record<string, string> = {
  draft:            'bg-slate-100 text-slate-700 border-slate-200',
  computed:         'bg-blue-50 text-blue-700 border-blue-200',
  pending_approval: 'bg-amber-50 text-amber-800 border-amber-200',
  approved:         'bg-violet-50 text-violet-700 border-violet-200',
  paid:             'bg-green-50 text-green-700 border-green-200',
  partially_paid:   'bg-teal-50 text-teal-700 border-teal-200',
  cancelled:        'bg-red-50 text-red-700 border-red-200',
};

type Tab = 'runs' | 'salaries' | 'config';

export function PayrollPage() {
  const qc = useQueryClient();
  const [tab, setTab] = useState<Tab>('runs');
  const [error, setError] = useState<string | null>(null);
  const [confirmModal, setConfirmModal] = useState<{
    title: string; message: string; confirmLabel?: string; destructive?: boolean; resolve: (v: boolean) => void;
  } | null>(null);
  const requestConfirm = (title: string, message: string, confirmLabel?: string, destructive = false) =>
    new Promise<boolean>((r) => setConfirmModal({ title, message, confirmLabel, destructive, resolve: r }));

  const [formModal, setFormModal] = useState<{
    title: string;
    description?: string;
    fields: FormField[];
    submitLabel?: string;
    action: (values: Record<string, any>) => Promise<void>;
  } | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  useEffect(() => {
    if (!successMsg) return;
    const t = setTimeout(() => setSuccessMsg(null), 4000);
    return () => clearTimeout(t);
  }, [successMsg]);
  const [showNewRun, setShowNewRun] = useState(false);
  const [openRun, setOpenRun] = useState<number | null>(null);
  const [editStaff, setEditStaff] = useState<any | null>(null);
  const [incrementStaff, setIncrementStaff] = useState<any | null>(null);
  const [bankStaff, setBankStaff] = useState<any | null>(null);
  const [pauseStaff, setPauseStaff] = useState<any | null>(null);
  const canPause = ['owner', 'bursar'].includes(String(useAuth((x) => x.user?.role)));
  const resume = async (s: any) => {
    if (!(await requestConfirm('Resume pay?', `${s.name} will be included again from the next payroll calculation.`, 'Resume pay'))) return;
    try { const r: any = await api.post(`/school/payroll/salaries/${s.user_id}/resume`); setSuccessMsg(r.data?.message ?? 'Pay resumed.'); salariesQ.refetch(); }
    catch (e: any) { setError(e?.response?.data?.message ?? 'Could not resume pay.'); }
  };
  const base = useBase();

  const runsQ = useQuery({
    queryKey: ['payroll-runs'],
    queryFn: () => getPayrollRuns({ per_page: 30 }).then(unwrap),
  });
  const salariesQ = useQuery({
    queryKey: ['payroll-salaries'],
    queryFn: () => getPayrollSalaries().then(unwrap),
    enabled: tab === 'salaries',
  });
  const gradesQ = useQuery({
    queryKey: ['salary-grades'],
    queryFn: () => getSalaryGrades().then(unwrap),
    enabled: tab === 'config' || tab === 'salaries',
  });
  const allowancesQ = useQuery({
    queryKey: ['payroll-allowances'],
    queryFn: () => getPayrollAllowances().then(unwrap),
    enabled: tab === 'config',
  });
  const deductionsQ = useQuery({
    queryKey: ['payroll-deductions'],
    queryFn: () => getPayrollDeductions().then(unwrap),
    enabled: tab === 'config',
  });

  const runs = arr<any>(runsQ.data?.data ?? runsQ.data);
  const salaries = arr<any>(salariesQ.data);
  const grades = arr<any>(gradesQ.data);
  const allowances = arr<any>(allowancesQ.data);
  const deductions = arr<any>(deductionsQ.data);

  const refreshAll = () => {
    runsQ.refetch(); salariesQ.refetch(); gradesQ.refetch(); allowancesQ.refetch(); deductionsQ.refetch();
  };

  const [pinModal, setPinModal] = useState<{
    title: string;
    subtitle?: string;
    resolve: (pin: string | null) => void;
  } | null>(null);
  const requestPin = (title: string, subtitle?: string) =>
    new Promise<string | null>((r) => setPinModal({ title, subtitle, resolve: r }));

  const computeM = useMutation({
    mutationFn: (id: number) => computePayrollRun(id),
    onSuccess: () => { runsQ.refetch(); setError(null); },
    onError: (e: any) => setError(e?.response?.data?.message ?? 'Compute failed'),
  });
  const submitM = useMutation({
    mutationFn: (id: number) => submitPayrollRun(id),
    onSuccess: () => { runsQ.refetch(); setError(null); },
    onError: (e: any) => setError(e?.response?.data?.message ?? 'Submit failed'),
  });
  const approveM = useMutation({
    mutationFn: ({ id, pin }: { id: number; pin: string }) => approvePayrollRun(id, pin),
    onSuccess: () => { runsQ.refetch(); setError(null); setSuccessMsg('Payroll approved.'); },
    onError: (e: any) => setError(e?.response?.data?.message ?? 'Approve failed'),
  });
  const payM = useMutation({
    mutationFn: ({ id, pin }: { id: number; pin: string }) => payPayrollRun(id, pin),
    onSuccess: (r: any) => {
      const d = unwrap(r);
      runsQ.refetch();
      const result = d?.result;
      if (result) {
        const msg = !result.payout_enabled
          ? `Money reserved for ${result.reserved ?? 0} staff. Payouts will go out when enabled.`
          : `Paid ${result.paid ?? 0} staff.${result.failed ? ` ${result.failed} failed.` : ''}${result.held ? ` ${result.held} held (no bank).` : ''}`;
        setSuccessMsg(msg);
      } else {
        setSuccessMsg('Payroll paid.');
      }
      setError(null);
    },
    onError: (e: any) => setError(e?.response?.data?.message ?? 'Pay failed'),
  });

  const deleteSalaryM = useMutation({
    mutationFn: (userId: number) => deletePayrollSalary(userId),
    onSuccess: () => { salariesQ.refetch(); setSuccessMsg('Salary removed.'); setError(null); },
    onError: (e: any) => setError(e?.response?.data?.message ?? 'Delete failed'),
  });

  return (
    <div className="max-w-[1400px] mx-auto p-6 space-y-6">
      <div className="flex justify-between items-end">
        <div>
          <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Finance</p>
          <h1 className="text-3xl font-extrabold text-slate-900">Payroll</h1>
          <p className="text-slate-500 text-sm mt-1">
            Prepare, approve, and pay staff salaries each month
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={refreshAll}
            className="px-3 py-2 text-sm font-semibold text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 inline-flex items-center gap-1">
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
          {tab === 'runs' && (
            <button onClick={() => setShowNewRun(true)}
              className="px-4 py-2 text-sm font-semibold text-white bg-[#0f172a] rounded-lg hover:bg-slate-800 inline-flex items-center gap-1">
              <Plus className="w-4 h-4" /> New payroll run
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="fixed top-4 right-4 z-[100] max-w-md">
          <div className="bg-red-600 text-white rounded-xl shadow-2xl p-4 flex items-start gap-3">
            <p className="text-sm font-semibold flex-1">{error}</p>
            <button onClick={() => setError(null)}><X className="w-4 h-4" /></button>
          </div>
        </div>
      )}
      {successMsg && (
        <div className="fixed top-4 right-4 z-[100] max-w-md">
          <div className="bg-green-600 text-white rounded-xl shadow-2xl p-4 flex items-start gap-3">
            <p className="text-sm font-semibold flex-1">{successMsg}</p>
            <button onClick={() => setSuccessMsg(null)}><X className="w-4 h-4" /></button>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-slate-200">
        <div className="flex gap-1 overflow-x-auto">
          {([
            { key: 'runs' as Tab, label: 'Payroll runs', icon: Wallet },
            { key: 'salaries' as Tab, label: 'Salaries', icon: Users },
            { key: 'config' as Tab, label: 'Allowances, deductions & grades', icon: FileText },
          ]).map(t => {
            const Icon = t.icon;
            return (
              <button key={t.key} onClick={() => setTab(t.key)}
                className={`px-4 py-3 text-sm font-semibold whitespace-nowrap border-b-2 inline-flex items-center gap-2 transition ${
                  tab === t.key ? 'border-[#0f172a] text-slate-900' : 'border-transparent text-slate-500 hover:text-slate-800'
                }`}>
                <Icon className="w-4 h-4" /> {t.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* ── RUNS TAB ── */}
      {tab === 'runs' && (
        <>
          {runsQ.isLoading ? (
            <div className="text-center py-8"><Loader2 className="w-5 h-5 animate-spin text-slate-400 mx-auto" /></div>
          ) : runs.length === 0 ? (
            <div className="bg-white p-12 rounded-xl border border-slate-200 text-center">
              <Wallet className="w-10 h-10 text-slate-300 mx-auto mb-2" />
              <p className="font-semibold text-slate-700">No payroll runs yet</p>
              <p className="text-sm text-slate-500 mt-1">
                Create a run to compute monthly staff salaries. Make sure every staff member has a salary set first.
              </p>
              <button onClick={() => setShowNewRun(true)}
                className="mt-4 px-4 py-2 text-sm font-semibold text-white bg-[#0f172a] rounded-lg hover:bg-slate-800 inline-flex items-center gap-1">
                <Plus className="w-4 h-4" /> Create first run
              </button>
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 font-bold text-xs uppercase tracking-wider border-b border-slate-200">
                  <tr>
                    <th className="px-3 py-3 text-left">Period</th>
                    <th className="px-3 py-3 text-left">Title</th>
                    <th className="px-3 py-3 text-right">Staff</th>
                    <th className="px-3 py-3 text-right">Gross</th>
                    <th className="px-3 py-3 text-right">Net</th>
                    <th className="px-3 py-3 text-left">Status</th>
                    <th className="px-3 py-3 text-right">Next step</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {runs.map((r: any) => {
                    const st = (r.status ?? 'draft').toLowerCase();
                    return (
                      <tr key={r.id} className="hover:bg-slate-50">
                        <td className="px-3 py-2 font-mono text-xs text-slate-600">
                          {String(r.period_month ?? '—').padStart(2, '0')}/{r.period_year ?? '—'}
                        </td>
                        <td className="px-3 py-2 font-medium text-slate-900">
                          <button onClick={() => setOpenRun(r.id)} className="hover:underline text-left">
                            {r.title ?? r.name ?? '—'}
                          </button>
                        </td>
                        <td className="px-3 py-2 text-right text-slate-700">
                          {r.staff ?? r.staff_count ?? r.total_staff ?? r.items_count ?? '—'}
                        </td>
                        <td className="px-3 py-2 text-right font-mono">{r.total_gross != null ? money(r.total_gross) : '—'}</td>
                        <td className="px-3 py-2 text-right font-mono font-bold">{r.total_net != null ? money(r.total_net) : '—'}</td>
                        <td className="px-3 py-2">
                          <span className={`inline-block px-2 py-0.5 text-xs font-bold rounded-full border ${statusTone[st] ?? 'bg-slate-100 text-slate-600'}`}>
                            {st.replace('_', ' ')}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-right">
                          {st === 'draft' && (
                            <button onClick={() => computeM.mutate(r.id)} disabled={computeM.isPending}
                              className="text-xs font-semibold text-blue-600 hover:underline inline-flex items-center gap-1 disabled:opacity-50">
                              <Play className="w-3.5 h-3.5" /> Compute
                            </button>
                          )}
                          {st === 'computed' && (
                            <div className="flex gap-2 justify-end">
                              <button onClick={() => computeM.mutate(r.id)} disabled={computeM.isPending}
                                className="text-xs font-semibold text-slate-600 hover:underline inline-flex items-center gap-1 disabled:opacity-50">
                                <Play className="w-3.5 h-3.5" /> Recompute
                              </button>
                              <button onClick={() => submitM.mutate(r.id)} disabled={submitM.isPending}
                                className="text-xs font-semibold text-blue-600 hover:underline inline-flex items-center gap-1 disabled:opacity-50">
                                <Send className="w-3.5 h-3.5" /> Submit
                              </button>
                            </div>
                          )}
                          {st === 'pending_approval' && (
                            <div className="flex gap-2 justify-end">
                              <button onClick={() => computeM.mutate(r.id)} disabled={computeM.isPending}
                                className="text-xs font-semibold text-slate-600 hover:underline inline-flex items-center gap-1 disabled:opacity-50">
                                Recompute
                              </button>
                              <button onClick={async () => {
                                const pin = await requestPin('Approve payroll', `Approve ${r.title ?? 'this payroll run'}`);
                                if (!pin) return;
                                approveM.mutate({ id: r.id, pin });
                              }} disabled={approveM.isPending}
                                className="text-xs font-semibold text-violet-700 hover:underline inline-flex items-center gap-1 disabled:opacity-50">
                                <Check className="w-3.5 h-3.5" /> Approve
                              </button>
                            </div>
                          )}
                          {(st === 'approved' || st === 'partially_paid') && (
                            <button onClick={async () => {
                              if (!await requestConfirm('Pay payroll?', 'Pay salaries from the school wallet? This debits real money.', 'Pay salaries')) return;
                              const pin = await requestPin('Confirm payment', 'Enter your 4-digit PIN to pay staff');
                              if (!pin) return;
                              payM.mutate({ id: r.id, pin });
                            }} disabled={payM.isPending}
                              className="text-xs font-semibold text-white bg-green-600 hover:bg-green-700 px-2 py-1 rounded inline-flex items-center gap-1 disabled:opacity-50">
                              <Banknote className="w-3.5 h-3.5" /> {st === 'partially_paid' ? ((r.paid ?? 0) > 0 || (r.reserved ?? 0) > 0 ? 'Pay rest' : 'Retry payment') : 'Pay now'}
                            </button>
                          )}
                          {st === 'paid' && (
                            <span className="text-xs text-green-600 font-semibold">✓ Complete</span>
                          )}
                          {st === 'cancelled' && (
                            <span className="text-xs text-red-500">Cancelled</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* ── SALARIES TAB ── */}
      {tab === 'salaries' && (
        <>
          {salaries.some((x: any) => !x.has_bank_account && x.base_salary != null && !x.paused) && (
            <p className="text-sm bg-amber-50 border border-amber-200 text-amber-900 rounded-xl px-3 py-2.5">
              {(() => { const n = salaries.filter((x: any) => !x.has_bank_account && x.base_salary != null && !x.paused).length; return `${n} staff member${n === 1 ? ' has' : 's have'} a salary but no bank account, so ${n === 1 ? 'their pay' : 'their pay'} would be held. `; })()}Tap <b>Add bank</b> on the row.
            </p>
          )}
          {base === '/owner' && <div className="flex justify-end">
            <a href="/owner/staff" className="text-xs font-semibold text-blue-600 hover:underline">
              Manage staff profiles →
            </a>
          </div>}
          {salariesQ.isLoading ? (
            <div className="text-center py-8"><Loader2 className="w-5 h-5 animate-spin text-slate-400 mx-auto" /></div>
          ) : salaries.length === 0 ? (
            <div className="bg-white p-12 rounded-xl border border-slate-200 text-center">
              <Users className="w-10 h-10 text-slate-300 mx-auto mb-2" />
              <p className="font-semibold text-slate-700">No active staff</p>
              <p className="text-sm text-slate-500 mt-1">Add teachers and staff first.</p>
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 font-bold text-xs uppercase tracking-wider border-b border-slate-200">
                  <tr>
                    <th className="px-3 py-3 text-left">Staff</th>
                    <th className="px-3 py-3 text-left">Role</th>
                    <th className="px-3 py-3 text-left">Designation</th>
                    <th className="px-3 py-3 text-right">Base salary</th>
                    <th className="px-3 py-3 text-left">Effective</th>
                    <th className="px-3 py-3 text-left">Bank</th>
                    <th className="px-3 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {salaries.map((s: any) => (
                    <tr key={s.user_id} className={`hover:bg-slate-50 ${s.paused ? 'bg-amber-50/40' : ''}`}>
                      <td className="px-3 py-2 font-medium text-slate-900">
                        <span className={s.paused ? 'text-slate-500' : ''}>{s.name}</span>
                        {s.paused && <span className="block text-[11px] font-semibold text-amber-800" title={s.pause_note ?? ''}>Pay paused · {({ suspended: 'Suspended', resigned: 'Resigned / left', leave: 'Unpaid leave', other: 'Other' } as any)[s.pause_reason] ?? s.pause_reason}{s.pause_note ? ` — ${s.pause_note}` : ''}</span>}
                      </td>
                      <td className="px-3 py-2 text-slate-600 capitalize text-xs">
                        {s.role}
                        {s.status === 'invited' && <span className="ml-1.5 text-[10px] text-amber-600 font-bold">INVITED</span>}
                      </td>
                      <td className="px-3 py-2 text-slate-600 text-xs">{s.designation ?? '—'}</td>
                      <td className="px-3 py-2 text-right font-mono">
                        {s.base_salary != null ? money(s.base_salary) : <span className="text-red-600">Not set</span>}
                      </td>
                      <td className="px-3 py-2 text-xs text-slate-600">{s.effective_from ? fmtDate(s.effective_from) : '—'}</td>
                      <td className="px-3 py-2 text-xs">
                        {s.has_bank_account ? (
                          <button onClick={() => setBankStaff(s)} title="Change the salary account" className="text-green-700 inline-flex items-center gap-1 hover:underline">
                            <Check className="w-3 h-3" /> {s.bank ?? 'Bank set'}
                          </button>
                        ) : (
                          <button onClick={() => setBankStaff(s)} className="px-2.5 py-1 rounded-lg bg-amber-100 text-amber-800 font-bold hover:bg-amber-200 inline-flex items-center gap-1"><Plus className="w-3 h-3" />Add bank</button>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        <button onClick={() => setEditStaff(s)}
                          className="text-xs font-semibold text-blue-600 hover:underline inline-flex items-center gap-1">
                          <Edit2 className="w-3.5 h-3.5" /> {s.base_salary != null ? 'Edit' : 'Set'} salary
                        </button>
                        <button onClick={() => setIncrementStaff(s)} className="text-xs font-semibold text-amber-600 hover:underline ml-2">+ Increment</button>
                        {canPause && s.role !== 'owner' && (s.paused
                          ? <button onClick={() => resume(s)} className="text-xs font-semibold text-green-700 hover:underline ml-2">Resume pay</button>
                          : <button onClick={() => setPauseStaff(s)} className="text-xs font-semibold text-slate-700 hover:underline ml-2">Pause pay</button>)}
                        {s.base_salary != null && (
                          <button
                            onClick={async () => {
                              const ok = await requestConfirm(
                                'Delete salary?',
                                `Remove ${s.name}'s current salary? Future payrolls won't include them until a new salary is set.`,
                                'Delete', true
                              );
                              if (ok) deleteSalaryM.mutate(s.user_id);
                            }}
                            disabled={deleteSalaryM.isPending}
                            className="text-xs font-semibold text-red-600 hover:underline ml-2 disabled:opacity-50"
                          >
                            Delete
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* ── CONFIG TAB ── */}
      {tab === 'config' && (
        <div className="space-y-6">
          <ConfigSection
            title="Salary grades"
            subtitle="Reusable templates for base salaries"
            items={grades}
            loading={gradesQ.isLoading}
            onAdd={() => setFormModal({
              title: 'Add salary grade',
              description: 'Reusable template for base salaries',
              submitLabel: 'Create grade',
              fields: [
                { name: 'name', label: 'Grade name', type: 'text', required: true, placeholder: 'e.g. Teacher Grade A' },
                { name: 'base_salary', label: 'Base salary', type: 'number', required: true, min: 0, step: 0.01, placeholder: '0.00' },
                { name: 'description', label: 'Description', type: 'text', placeholder: 'Optional note' },
              ],
              action: async (values) => {
                await postSalaryGrade({
                  name: String(values.name).trim(),
                  base_salary: Number(values.base_salary),
                  description: values.description ? String(values.description).trim() : undefined,
                });
                gradesQ.refetch();
                setSuccessMsg('Grade added.');
                setFormModal(null);
              },
            })}
            renderRow={(g: any) => (
              <>
                <p className="font-medium text-slate-900">{g.name}</p>
                <p className="text-xs text-slate-500">{money(g.base_salary)}</p>
              </>
            )}
            emptyMsg="No salary grades yet."
          />

          <ConfigSection
            title="Allowances"
            subtitle="Fixed amounts or percentages added to salary"
            items={allowances}
            loading={allowancesQ.isLoading}
            onAdd={() => setFormModal({
              title: 'Add allowance',
              description: 'Fixed amounts or percentages added to salary',
              submitLabel: 'Create allowance',
              fields: [
                { name: 'name', label: 'Allowance name', type: 'text', required: true, placeholder: 'e.g. Housing, Transport' },
                { name: 'type', label: 'Type', type: 'select', required: true, placeholder: 'Choose type', options: [
                  { label: 'Fixed amount (₦)', value: 'fixed' },
                  { label: 'Percentage of base', value: 'percent' },
                ]},
                { name: 'value', label: 'Value', type: 'number', required: true, min: 0, step: 0.01, placeholder: 'Amount or %' },
                { name: 'applies_to', label: 'Applies to', type: 'select', defaultValue: 'teacher', options: [
                  { label: 'Teachers only', value: 'teacher' },
                  { label: 'All staff', value: 'all' },
                ]},
                { name: 'is_active', label: 'Active', type: 'checkbox', defaultValue: true },
              ],
              action: async (values) => {
                await postPayrollAllowance({
                  name: String(values.name).trim(),
                  type: String(values.type),
                  value: Number(values.value),
                  applies_to: values.applies_to || 'teacher',
                  is_active: !!values.is_active,
                });
                allowancesQ.refetch();
                setSuccessMsg('Allowance added.');
                setFormModal(null);
              },
            })}
            renderRow={(a: any) => (
              <>
                <p className="font-medium text-slate-900">{a.name}</p>
                <p className="text-xs text-slate-500">
                  {a.type === 'percent' ? `${a.value}%` : money(a.value)} · {a.applies_to ?? 'teacher'}
                  {a.person && <span className="block mt-1 text-slate-700 font-semibold">Target: {a.person}</span>}
                </p>
              </>
            )}
            emptyMsg="No allowances yet."
          />

          <ConfigSection
            title="Deductions"
            subtitle="Pension, tax, loans, and other deductions"
            items={deductions}
            loading={deductionsQ.isLoading}
            onAdd={() => setFormModal({
              title: 'Add deduction',
              description: 'Pension, tax, loans, and other deductions',
              submitLabel: 'Create deduction',
              fields: [
                { name: 'name', label: 'Deduction name', type: 'text', required: true, placeholder: 'e.g. Pension, Cooperative' },
                { name: 'type', label: 'Type', type: 'select', required: true, placeholder: 'Choose type', options: [
                  { label: 'Fixed amount (₦)', value: 'fixed' },
                  { label: 'Percentage of base', value: 'percent' },
                  { label: 'Loan repayment', value: 'loan' },
                  { label: 'Tax', value: 'tax' },
                  { label: 'Pension', value: 'pension' },
                ]},
                { name: 'value', label: 'Value', type: 'number', required: true, min: 0, step: 0.01, placeholder: 'Amount or %' },
                { name: 'applies_to', label: 'Applies to', type: 'select', defaultValue: 'teacher', options: [
                  { label: 'Teachers only', value: 'teacher' },
                  { label: 'All staff', value: 'all' },
                ]},
                { name: 'is_active', label: 'Active', type: 'checkbox', defaultValue: true },
              ],
              action: async (values) => {
                await postPayrollDeduction({
                  name: String(values.name).trim(),
                  type: String(values.type),
                  value: Number(values.value),
                  applies_to: values.applies_to || 'teacher',
                  is_active: !!values.is_active,
                });
                deductionsQ.refetch();
                setSuccessMsg('Deduction added.');
                setFormModal(null);
              },
            })}
            renderRow={(d: any) => (
              <>
                <p className="font-medium text-slate-900">{d.name}</p>
                <p className="text-xs text-slate-500">
                  {['percent', 'tax', 'pension'].includes(d.type) ? `${d.value}%` : money(d.value)} · {d.type}
                  {d.person && <span className="block mt-1 text-slate-700 font-semibold">Target: {d.person}</span>}
                </p>
              </>
            )}
            emptyMsg="No deductions yet."
          />
        </div>
      )}

      {/* Modals */}
      {showNewRun && (
        <NewPayrollRunModal
          onClose={() => setShowNewRun(false)}
          onSuccess={() => {
            setShowNewRun(false);
            runsQ.refetch();
            setSuccessMsg('Payroll run created. Click Compute to calculate.');
          }}
          onError={(m) => setError(m)}
        />
      )}
      {editStaff && (
        <SetSalaryModal
          staff={editStaff}
          grades={grades}
          onClose={() => setEditStaff(null)}
          onSuccess={() => {
            setEditStaff(null);
            salariesQ.refetch();
            setSuccessMsg('Salary saved.');
          }}
          onError={(m) => setError(m)}
        />
      )}

      {pinModal && (
        <PinModal
          title={pinModal.title}
          subtitle={pinModal.subtitle}
          onClose={() => { pinModal.resolve(null); setPinModal(null); }}
          onConfirm={(pin) => { pinModal.resolve(pin); setPinModal(null); }}
        />
      )}

      {formModal && (
        <FormModal
          title={formModal.title}
          description={formModal.description}
          fields={formModal.fields}
          submitLabel={formModal.submitLabel}
          onClose={() => setFormModal(null)}
          onSubmit={formModal.action}
        />
      )}

      {confirmModal && (
        <ConfirmModal
          title={confirmModal.title}
          message={confirmModal.message}
          confirmLabel={confirmModal.confirmLabel}
          destructive={confirmModal.destructive}
          onClose={() => { confirmModal.resolve(false); setConfirmModal(null); }}
          onConfirm={() => { confirmModal.resolve(true); setConfirmModal(null); }}
        />
      )}

      {pauseStaff && <PausePayModal staff={pauseStaff} onClose={() => setPauseStaff(null)} onDone={(m) => { setPauseStaff(null); setSuccessMsg(m); salariesQ.refetch(); }} />}
      {bankStaff && <StaffBankModal staff={{ user_id: bankStaff.user_id, name: bankStaff.name, bank: bankStaff.bank }} onClose={() => setBankStaff(null)} onSaved={(m) => { setBankStaff(null); setSuccessMsg(m); qc.invalidateQueries({ queryKey: ['owner-payroll-salaries'] }); salariesQ.refetch(); }} />}
      {incrementStaff && (
        <GiveIncrementModal
          staff={incrementStaff}
          onClose={() => setIncrementStaff(null)}
          onSuccess={() => {
            setIncrementStaff(null);
            salariesQ.refetch();
            setError(null);
          }}
          onError={(m) => setError(m)}
        />
      )}
      {openRun && (
        <PayrollRunDetailModal
          runId={openRun}
          onClose={() => setOpenRun(null)}
        />
      )}
    </div>
  );
}

function ConfigSection({ title, subtitle, items, loading, onAdd, renderRow, emptyMsg }: {
  title: string; subtitle: string; items: any[]; loading: boolean;
  onAdd: () => void; renderRow: (item: any) => React.ReactNode; emptyMsg: string;
}) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
      <div className="px-5 py-4 border-b border-slate-100 flex justify-between items-center">
        <div>
          <p className="font-bold text-slate-900">{title}</p>
          <p className="text-xs text-slate-500">{subtitle}</p>
        </div>
        <button onClick={onAdd}
          className="text-xs font-bold bg-[#0f172a] text-white px-3 py-1.5 rounded-lg inline-flex items-center gap-1">
          <Plus className="w-3 h-3" /> Add
        </button>
      </div>
      {loading ? (
        <div className="p-6 text-center"><Loader2 className="w-4 h-4 animate-spin text-slate-400 mx-auto" /></div>
      ) : items.length === 0 ? (
        <p className="p-6 text-sm text-slate-500 italic text-center">{emptyMsg}</p>
      ) : (
        <ul className="divide-y divide-slate-100">
          {items.map((item: any) => (
            <li key={item.id} className="px-5 py-3">{renderRow(item)}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

function NewPayrollRunModal({ onClose, onSuccess, onError }: {
  onClose: () => void; onSuccess: () => void; onError: (msg: string) => void;
}) {
  const now = new Date();
  const [month, setMonth] = useState(String(now.getMonth() + 1));
  const [year, setYear] = useState(String(now.getFullYear()));
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    const m = Number(month);
    const y = Number(year);
    if (!m || m < 1 || m > 12) return onError('Month must be 1-12');
    if (!y || y < 2024) return onError('Year must be 2024 or later');
    setSubmitting(true);
    try {
      await postPayrollRun({ month: m, year: y });
      onSuccess();
    } catch (e: any) {
      onError(e?.response?.data?.message ?? 'Create failed');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-md rounded-2xl shadow-xl">
        <div className="px-6 py-4 border-b border-slate-200 flex justify-between items-center bg-slate-50 rounded-t-2xl">
          <h3 className="font-bold text-slate-900">New payroll run</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">Month *</label>
              <select value={month} onChange={(e) => setMonth(e.target.value)}
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white">
                {['January','February','March','April','May','June','July','August','September','October','November','December'].map((n, i) => (
                  <option key={i} value={String(i + 1)}>{n}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">Year *</label>
              <input type="number" value={year} onChange={(e) => setYear(e.target.value)} min={2024} max={2100}
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
            </div>
          </div>
          <p className="text-xs text-slate-500">
            You can only prepare payroll up to one month in advance. Once created, click Compute on the run to calculate salaries.
          </p>
        </div>
        <div className="px-6 py-4 border-t border-slate-200 flex gap-3 bg-slate-50 rounded-b-2xl">
          <button onClick={onClose}
            className="flex-1 py-2.5 border border-slate-300 rounded-lg text-slate-700 font-semibold text-sm">
            Cancel
          </button>
          <button onClick={submit} disabled={submitting}
            className="flex-1 py-2.5 bg-[#0f172a] text-white rounded-lg font-semibold text-sm disabled:opacity-50">
            {submitting ? 'Creating…' : 'Create run'}
          </button>
        </div>
      </div>
    </div>
  );
}

function SetSalaryModal({ staff, grades, onClose, onSuccess, onError }: {
  staff: any; grades: any[]; onClose: () => void; onSuccess: () => void; onError: (msg: string) => void;
}) {
  const [mode, setMode] = useState<'amount' | 'grade'>(staff.salary_grade_id ? 'grade' : 'amount');
  const [amount, setAmount] = useState(staff.base_salary ? String(staff.base_salary) : '');
  const [gradeId, setGradeId] = useState<number | ''>(staff.salary_grade_id ?? '');
  const [effective, setEffective] = useState(new Date().toISOString().slice(0, 10));
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    setSubmitting(true);
    try {
      if (mode === 'amount') {
        const amt = Number(amount);
        if (!amt || amt < 0) return onError('Amount must be a number');
        await putPayrollSalary(staff.user_id, { base_salary: amt, effective_from: effective });
      } else {
        if (!gradeId) return onError('Pick a grade');
        await putPayrollSalary(staff.user_id, { salary_grade_id: Number(gradeId), effective_from: effective });
      }
      onSuccess();
    } catch (e: any) {
      onError(e?.response?.data?.message ?? 'Save failed');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-md rounded-2xl shadow-xl">
        <div className="px-6 py-4 border-b border-slate-200 flex justify-between items-center bg-slate-50 rounded-t-2xl">
          <div>
            <h3 className="font-bold text-slate-900">Set salary</h3>
            <p className="text-xs text-slate-500">{staff.name} · {staff.role}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-3">
          <div className="inline-flex bg-slate-100 rounded-lg p-1">
            <button onClick={() => setMode('amount')}
              className={`px-3 py-1.5 text-xs font-bold rounded ${mode === 'amount' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600'}`}>
              Direct amount
            </button>
            <button onClick={() => setMode('grade')}
              className={`px-3 py-1.5 text-xs font-bold rounded ${mode === 'grade' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600'}`}>
              Use grade
            </button>
          </div>
          {mode === 'amount' ? (
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">Base salary (₦) *</label>
              <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)}
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
            </div>
          ) : (
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">Salary grade *</label>
              <select value={gradeId} onChange={(e) => setGradeId(e.target.value ? Number(e.target.value) : '')}
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white">
                <option value="">Pick a grade…</option>
                {grades.map((g: any) => (
                  <option key={g.id} value={g.id}>{g.name} — {money(g.base_salary)}</option>
                ))}
              </select>
              {grades.length === 0 && (
                <p className="text-xs text-amber-700 mt-1">No grades exist. Create one in Config tab first.</p>
              )}
            </div>
          )}
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1">Effective from *</label>
            <input type="date" value={effective} onChange={(e) => setEffective(e.target.value)}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
          </div>
        </div>
        <div className="px-6 py-4 border-t border-slate-200 flex gap-3 bg-slate-50 rounded-b-2xl">
          <button onClick={onClose}
            className="flex-1 py-2.5 border border-slate-300 rounded-lg text-slate-700 font-semibold text-sm">Cancel</button>
          <button onClick={submit} disabled={submitting}
            className="flex-1 py-2.5 bg-[#0f172a] text-white rounded-lg font-semibold text-sm disabled:opacity-50">
            {submitting ? 'Saving…' : 'Save salary'}
          </button>
        </div>
      </div>
    </div>
  );
}

function PayrollRunDetailModal({ runId, onClose }: { runId: number; onClose: () => void }) {
  const runQ = useQuery({
    queryKey: ['payroll-run', runId],
    queryFn: () => getPayrollRunById(runId).then(unwrap),
  });
  const run = runQ.data;
  const items = arr<any>(run?.items ?? run?.lines ?? run?.employees);

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 overflow-y-auto">
      <div className="min-h-screen flex items-start justify-center p-4 py-8">
        <div className="bg-white w-full max-w-3xl rounded-2xl shadow-xl">
          <div className="px-6 py-4 border-b border-slate-200 flex justify-between items-center bg-slate-50 rounded-t-2xl">
            <div>
              <h3 className="font-bold text-slate-900">{run?.title ?? 'Payroll run'}</h3>
              <p className="text-xs text-slate-500">
                {String(run?.period_month ?? '').padStart(2, '0')}/{run?.period_year} · {run?.status}
              </p>
            </div>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
          </div>
          <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
            {runQ.isLoading ? (
              <p className="text-slate-500 text-sm">Loading…</p>
            ) : !run ? (
              <p className="text-red-600 text-sm">Could not load run.</p>
            ) : (
              <>
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-slate-50 rounded-lg p-3">
                    <p className="text-xs font-semibold text-slate-500">Gross</p>
                    <p className="text-lg font-extrabold font-mono">{money(run.total_gross)}</p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-3">
                    <p className="text-xs font-semibold text-slate-500">Deductions</p>
                    <p className="text-lg font-extrabold font-mono">{money(run.total_deductions)}</p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-3">
                    <p className="text-xs font-semibold text-slate-500">Net</p>
                    <p className="text-lg font-extrabold font-mono text-green-700">{money(run.total_net)}</p>
                  </div>
                </div>
                {items.length > 0 ? (
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-slate-500 text-xs uppercase border-b">
                      <tr>
                        <th className="px-2 py-2 text-left">Staff</th>
                        <th className="px-2 py-2 text-right">Gross</th>
                        <th className="px-2 py-2 text-right">Deductions</th>
                        <th className="px-2 py-2 text-right">Net</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {items.map((it: any) => (
                        <tr key={it.id}>
                          <td className="px-2 py-1.5">{it.name ?? it.staff_name ?? `User ${it.user_id}`}</td>
                          <td className="px-2 py-1.5 text-right font-mono">{money(it.gross)}</td>
                          <td className="px-2 py-1.5 text-right font-mono">{money(it.deductions ?? it.total_deductions)}</td>
                          <td className="px-2 py-1.5 text-right font-mono font-bold">{money(it.net ?? it.total_net)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p className="text-sm text-slate-500 italic text-center py-4">
                    No per-staff breakdown available. Run the compute step first.
                  </p>
                )}
              </>
            )}
          </div>
          <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-3 bg-slate-50 rounded-b-2xl">
            <button onClick={onClose}
              className="px-4 py-2 text-sm font-semibold text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50">
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function GiveIncrementModal({ staff, onClose, onSuccess, onError }: {
  staff: any; onClose: () => void; onSuccess: () => void; onError: (msg: string) => void;
}) {
  const current = Number(staff.base_salary ?? 0);
  const [newSalary, setNewSalary] = useState(current > 0 ? String(Math.round(current * 1.1)) : '');
  const [effectiveFrom, setEffectiveFrom] = useState(new Date().toISOString().slice(0, 10));
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const next = Number(newSalary || 0);
  const pct = current > 0 && next > 0 ? ((next - current) / current) * 100 : 0;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (next <= 0) return onError('New salary must be greater than 0');
    if (current > 0 && next <= current) return onError('New salary must be higher than the current salary');
    setSubmitting(true);
    try {
      await postPayrollIncrement({
        user_id: staff.user_id ?? staff.id,
        new_salary: next,
        effective_from: effectiveFrom,
        reason: reason.trim() || undefined,
      });
      onSuccess();
    } catch (e: any) {
      onError(e?.response?.data?.message ?? 'Increment failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 overflow-y-auto">
      <div className="min-h-screen flex items-start justify-center p-4 py-8">
        <div className="bg-white w-full max-w-md rounded-2xl shadow-xl">
          <div className="px-6 py-4 border-b border-slate-200 flex justify-between items-center bg-slate-50 rounded-t-2xl">
            <div>
              <h3 className="font-bold text-slate-900">Give salary increment</h3>
              <p className="text-xs text-slate-500">{staff.name ?? staff.full_name ?? staff.email ?? `User #${staff.user_id ?? staff.id}`}</p>
            </div>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
          </div>
          <form onSubmit={submit} className="p-6 space-y-3">
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm">
              <div className="flex justify-between"><span className="text-slate-500">Current salary</span><b className="font-mono">{money(current)}</b></div>
              {next > 0 && (
                <div className="flex justify-between mt-1 text-green-700">
                  <span>New salary</span>
                  <b className="font-mono">{money(next)} {pct !== 0 && `(${pct > 0 ? '+' : ''}${pct.toFixed(1)}%)`}</b>
                </div>
              )}
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">New monthly salary *</label>
              <input type="number" min="1" value={newSalary} onChange={(e) => setNewSalary(e.target.value)} required
                className="w-full border border-slate-300 rounded-lg p-2.5 text-sm" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">Effective from *</label>
              <input type="date" value={effectiveFrom} onChange={(e) => setEffectiveFrom(e.target.value)} required
                className="w-full border border-slate-300 rounded-lg p-2.5 text-sm" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">Reason</label>
              <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Annual performance review"
                className="w-full border border-slate-300 rounded-lg p-2.5 text-sm" />
            </div>
            <div className="flex gap-3 pt-2">
              <button type="button" onClick={onClose} className="flex-1 py-2.5 border border-slate-300 rounded-lg font-semibold text-sm">Cancel</button>
              <button type="submit" disabled={submitting}
                className="flex-1 py-2.5 bg-[#0f172a] text-white rounded-lg font-semibold text-sm disabled:opacity-50">
                {submitting ? 'Saving…' : 'Apply increment'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
